import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { YesNoRow } from "../ui/yes-no-toggle";
import { Button } from "../ui/button";
import { Briefcase, Plus, X, AlertTriangle } from "lucide-react";
import RequiredLabel from "../ui/RequiredLabel";
import { incomeTypes } from "../../utils/mockData";

const IncomeSection = ({ data, onChange }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleIncomeChange = (index, field, value) => {
    const newIncomes = [...data.incomes];
    newIncomes[index] = { ...newIncomes[index], [field]: value };
    onChange({ incomes: newIncomes });
  };

  const addIncome = () => {
    onChange({ incomes: [...data.incomes, { type: "Salariu", value: "" }] });
  };

  const removeIncome = (index) => {
    onChange({ incomes: data.incomes.filter((_, i) => i !== index) });
  };

  // Calculul BNR 20% - compară venitul curent cu media anului trecut
  const bnrWarning = useMemo(() => {
    const currentIncome = parseFloat(data.incomes?.[0]?.value) || 0;
    const lastYearAvg = parseFloat(data.lastYearAvgIncome) || 0;
    if (currentIncome > 0 && lastYearAvg > 0) {
      const ratio = currentIncome / lastYearAvg;
      const percentIncrease = Math.round((ratio - 1) * 100);
      if (ratio > 1.20) {
        return {
          show: true,
          percentIncrease,
          maxAccepted: Math.round(lastYearAvg * 1.20),
          message: `Venitul curent (${currentIncome} LEI) depășește cu ${percentIncrease}% media anului trecut (${lastYearAvg} LEI). Conform normei BNR, creșterea maximă acceptată fără justificare este de 20%. Venitul maxim acceptat: ${Math.round(lastYearAvg * 1.20)} LEI/lună. Este necesară justificarea creșterii venitului.`
        };
      }
    }
    return { show: false };
  }, [data.incomes, data.lastYearAvgIncome]);

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 border-b">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Briefcase className="h-5 w-5" />
          Venituri
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Venituri principale */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Venituri Principale</h3>
              <Button type="button" onClick={addIncome} variant="outline" size="sm" className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Adaugă Venit
              </Button>
            </div>

            {data.incomes.map((income, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <RequiredLabel htmlFor={`incomeType${index}`}>Tip Venit {index + 1}</RequiredLabel>
                    <Select
                      value={income.type}
                      onValueChange={(value) => handleIncomeChange(index, 'type', value)}
                    >
                      <SelectTrigger data-testid={`income-type-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {incomeTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <RequiredLabel htmlFor={`incomeValue${index}`}>Venit Net Lunar (LEI/lună)</RequiredLabel>
                    <Input
                      id={`incomeValue${index}`}
                      data-testid={`income-value-${index}`}
                      type="number"
                      value={income.value}
                      onChange={(e) => handleIncomeChange(index, 'value', e.target.value)}
                      placeholder="Venitul net pe lună"
                    />
                    <p className="text-xs text-gray-400">Venitul net lunar curent</p>
                  </div>

                  <div className="flex items-end">
                    {data.incomes.length > 1 && (
                      <Button type="button" onClick={() => removeIncome(index)} variant="destructive" size="sm">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Media venit anul trecut - Norma BNR */}
          <div className="p-4 border border-blue-200 rounded-lg bg-blue-50/50">
            <h4 className="text-sm font-semibold text-blue-900 mb-3">
              Verificare Norma BNR — Creștere Venit
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <RequiredLabel htmlFor="lastYearAvgIncome">Media Venitului Net Lunar — Anul Trecut (LEI/lună)</RequiredLabel>
                <Input
                  id="lastYearAvgIncome"
                  data-testid="lastYearAvgIncome"
                  type="number"
                  value={data.lastYearAvgIncome || ""}
                  onChange={(e) => handleInputChange('lastYearAvgIncome', e.target.value)}
                  placeholder="Media lunară a venitului net anul trecut"
                />
                <p className="text-xs text-gray-500">
                  Media venitului net lunar din anul precedent (suma veniturilor / 12 luni)
                </p>
              </div>
              <div className="flex items-center">
                {data.incomes?.[0]?.value && data.lastYearAvgIncome && !bnrWarning.show && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg w-full text-sm text-green-800">
                    Creșterea venitului este sub 20% — în conformitate cu norma BNR.
                  </div>
                )}
              </div>
            </div>

            {bnrWarning.show && (
              <div className="mt-3 p-3 bg-amber-50 border-2 border-amber-400 rounded-lg flex gap-3 items-start">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-bold mb-1">Atenție — Creștere venit &gt;20% (Norma BNR)</p>
                  <p>{bnrWarning.message}</p>
                  <div className="mt-2 space-y-2">
                    <RequiredLabel htmlFor="incomeJustification" className="text-xs font-semibold">
                      Justificare creștere venit
                    </RequiredLabel>
                    <Textarea
                      id="incomeJustification"
                      data-testid="incomeJustification"
                      value={data.incomeJustification || ""}
                      onChange={(e) => handleInputChange('incomeJustification', e.target.value)}
                      placeholder="Ex: Promovare, mărire salarială conform actului adițional din data..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Alte beneficii */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="mealVouchers">Bonuri de Masă / Normă de Hrană (LEI/lună)</Label>
              <Input
                id="mealVouchers"
                type="number"
                value={data.mealVouchers}
                onChange={(e) => handleInputChange('mealVouchers', e.target.value)}
                placeholder="Valoarea lunară"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="otherSources">Alte Surse de Venit</Label>
              <Textarea
                id="otherSources"
                value={data.otherSources}
                onChange={(e) => handleInputChange('otherSources', e.target.value)}
                placeholder="Descrieți alte surse de venit (chirii, dividende, etc.)"
                rows={3}
              />
            </div>
          </div>

          {/* Întrebări despre venituri */}
          <div className="space-y-2 divide-y">
            <YesNoRow
              label="Aveți venituri din străinătate?"
              value={data.foreignIncome ?? null}
              onChange={(v) => handleInputChange('foreignIncome', v)}
              testid="foreign-income"
            />
            <YesNoRow
              label="Aveți venituri de la firma proprie?"
              value={data.ownCompanyIncome ?? null}
              onChange={(v) => handleInputChange('ownCompanyIncome', v)}
              testid="own-company-income"
            />
          </div>

          {data.foreignIncome && (
            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
              <Label htmlFor="foreignIncomeDetails">Detalii Venituri din Străinătate</Label>
              <Textarea
                id="foreignIncomeDetails"
                value={data.foreignIncomeDetails || ""}
                onChange={(e) => handleInputChange('foreignIncomeDetails', e.target.value)}
                placeholder="Descrieți sursa, țara și valoarea veniturilor din străinătate"
                rows={3}
                className="mt-2"
              />
            </div>
          )}

          {data.ownCompanyIncome && (
            <div className="p-4 border border-green-200 rounded-lg bg-green-50">
              <Label htmlFor="ownCompanyDetails">Detalii Venituri Firmă Proprie</Label>
              <Textarea
                id="ownCompanyDetails"
                value={data.ownCompanyDetails || ""}
                onChange={(e) => handleInputChange('ownCompanyDetails', e.target.value)}
                placeholder="Descrieți tipul activității și valoarea veniturilor de la firma proprie"
                rows={3}
                className="mt-2"
              />
            </div>
          )}
        </div>

        {data.incomes.length === 1 && (
          <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="confirmedSingleIncome"
                checked={data.confirmedSingleIncome || false}
                onCheckedChange={(checked) => handleInputChange('confirmedSingleIncome', checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="confirmedSingleIncome" className="font-semibold text-base cursor-pointer text-yellow-900">
                  Confirm că acesta este singurul meu venit
                </Label>
                <p className="text-sm text-yellow-800 mt-1">
                  Dacă aveți și alte venituri, adăugați-le cu "Adaugă Venit".
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IncomeSection;
